import React, { useEffect, useState } from 'react';
import { managerApi, lookupApi, projectApi, activityApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function ApprovalsPage() {
  const { isHR } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [comments, setComments] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editEntries, setEditEntries] = useState([]);
  const [trcCodes, setTrcCodes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [message, setMessage] = useState(null);
  const [showSubmitOnBehalf, setShowSubmitOnBehalf] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [behalfWeek, setBehalfWeek] = useState('');
  const [behalfEntries, setBehalfEntries] = useState([]);

  useEffect(() => { loadPending(); loadLookups(); loadTeam(); }, []);

  const loadLookups = async () => {
    try {
      const [trcRes, projRes, actRes] = await Promise.all([
        lookupApi.trc(), projectApi.list(), activityApi.list()
      ]);
      if (trcRes.data.success) setTrcCodes(trcRes.data.data || []);
      if (projRes.data.success) setProjects(projRes.data.data || []);
      if (actRes.data.success) setActivities(actRes.data.data || []);
    } catch (err) { console.error(err); }
  };

  const loadTeam = async () => {
    try {
      const res = await managerApi.getTeam();
      if (res.data.success) setTeamMembers(res.data.data || []);
    } catch (err) { console.error(err); }
  };

  const loadPending = async () => {
    try {
      const res = await managerApi.getPending();
      if (res.data.success) setPending(res.data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleAction = async (timesheetId, action) => {
    setActionLoading(timesheetId);
    try {
      if (action === 'approve') {
        await managerApi.approve(timesheetId, comments);
        setMessage({ type: 'success', text: 'Timesheet approved!' });
      } else {
        if (!comments.trim()) {
          alert('Please provide a reason for rejection');
          setActionLoading(null);
          return;
        }
        await managerApi.reject(timesheetId, comments);
        setMessage({ type: 'success', text: 'Timesheet rejected and sent back to employee.' });
      }
      setComments('');
      setSelectedId(null);
      setEditingId(null);
      loadPending();
    } catch (err) {
      alert(err.response?.data?.message || 'Action failed');
    }
    setActionLoading(null);
  };

  // Start editing a timesheet
  const startEdit = (ts) => {
    setEditingId(ts.id);
    setSelectedId(ts.id);
    setEditEntries((ts.entries || []).map(e => ({
      trcCode: e.trcCode || 'REG',
      projectId: e.projectId || '',
      activityId: e.activityId || '',
      taskgroup: e.taskgroup || 'QPSUS',
      businessUnit: e.businessUnit || 'TEC00',
      hrDepartment: e.hrDepartment || '3697',
      hours: e.hours || [0, 0, 0, 0, 0, 0, 0],
    })));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditEntries([]);
  };

  const updateEditHour = (rowIdx, dayIdx, value) => {
    const val = parseFloat(value) || 0;
    setEditEntries(prev => prev.map((row, i) =>
      i === rowIdx ? { ...row, hours: row.hours.map((h, d) => d === dayIdx ? val : h) } : row
    ));
  };

  const updateEditField = (rowIdx, field, value) => {
    setEditEntries(prev => prev.map((row, i) =>
      i === rowIdx ? { ...row, [field]: value } : row
    ));
  };

  const addEditRow = () => {
    setEditEntries(prev => [...prev, {
      trcCode: 'REG', projectId: '', activityId: '',
      taskgroup: 'QPSUS', businessUnit: 'TEC00', hrDepartment: '3697',
      hours: [0, 0, 0, 0, 0, 0, 0],
    }]);
  };

  const removeEditRow = (idx) => {
    if (editEntries.length > 1) setEditEntries(prev => prev.filter((_, i) => i !== idx));
  };

  const getEditRowTotal = (row) => row.hours.reduce((s, h) => s + (h || 0), 0);
  const getEditGrandTotal = () => editEntries.reduce((s, row) => s + getEditRowTotal(row), 0);

  // Save edits and keep as SUBMITTED
  const saveEditAndKeep = async (ts) => {
    setActionLoading(ts.id);
    try {
      await managerApi.editTimesheet(ts.employeeId, ts.weekStartDate, editEntries, comments || 'Edited by manager');
      setMessage({ type: 'success', text: 'Timesheet updated. You can now approve it.' });
      setEditingId(null);
      setEditEntries([]);
      setComments('');
      loadPending();
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving edits');
    }
    setActionLoading(null);
  };

  // Save edits and approve in one step
  const saveEditAndApprove = async (ts) => {
    setActionLoading(ts.id);
    try {
      await managerApi.editTimesheet(ts.employeeId, ts.weekStartDate, editEntries, comments || 'Edited and approved by manager');
      // Now approve
      await managerApi.approve(ts.id, comments || 'Edited and approved by manager');
      setMessage({ type: 'success', text: 'Timesheet edited and approved!' });
      setEditingId(null);
      setEditEntries([]);
      setComments('');
      loadPending();
    } catch (err) {
      alert(err.response?.data?.message || 'Error');
    }
    setActionLoading(null);
  };

  const getStatusBadge = (status) => {
    const map = { SUBMITTED: 'badge-warning', MANAGER_APPROVED: 'badge-success' };
    return map[status] || 'badge-gray';
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const selectStyle = { padding: '3px 4px', fontSize: 12, minWidth: 0 };

  // ── Submit on Behalf helpers ──
  const createEmptyBehalfRow = () => ({
    trcCode: 'REG', projectId: '', activityId: '',
    taskgroup: 'QPSUS', businessUnit: 'TEC00', hrDepartment: '3697',
    hours: [0, 0, 0, 0, 0, 0, 0],
  });

  const openSubmitOnBehalf = () => {
    // Default to current week Monday
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((day + 6) % 7));
    const dateStr = monday.toISOString().split('T')[0];
    setBehalfWeek(dateStr);
    setBehalfEntries([createEmptyBehalfRow()]);
    setShowSubmitOnBehalf(true);
  };

  const updateBehalfHour = (rowIdx, dayIdx, value) => {
    const val = parseFloat(value) || 0;
    setBehalfEntries(prev => prev.map((row, i) =>
      i === rowIdx ? { ...row, hours: row.hours.map((h, d) => d === dayIdx ? val : h) } : row
    ));
  };

  const updateBehalfField = (rowIdx, field, value) => {
    setBehalfEntries(prev => prev.map((row, i) =>
      i === rowIdx ? { ...row, [field]: value } : row
    ));
  };

  const addBehalfRow = () => setBehalfEntries(prev => [...prev, createEmptyBehalfRow()]);
  const removeBehalfRow = (idx) => {
    if (behalfEntries.length > 1) setBehalfEntries(prev => prev.filter((_, i) => i !== idx));
  };
  const getBehalfRowTotal = (row) => row.hours.reduce((s, h) => s + (h || 0), 0);
  const getBehalfGrandTotal = () => behalfEntries.reduce((s, row) => s + getBehalfRowTotal(row), 0);

  const submitOnBehalf = async (action) => {
    if (!selectedEmpId) { alert('Please select a team member'); return; }
    if (!behalfWeek) { alert('Please select a week'); return; }
    setActionLoading('behalf');
    try {
      await managerApi.editTimesheet(
        parseInt(selectedEmpId), behalfWeek, behalfEntries,
        comments || 'Submitted by manager on behalf of employee',
        action
      );
      setMessage({ type: 'success', text: action === 'SUBMIT' ? 'Timesheet submitted on behalf of employee!' : 'Timesheet saved as draft for employee.' });
      setShowSubmitOnBehalf(false);
      setBehalfEntries([]);
      setSelectedEmpId('');
      setComments('');
      loadPending();
    } catch (err) {
      alert(err.response?.data?.message || 'Error submitting timesheet');
    }
    setActionLoading(null);
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>Pending Approvals</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="badge badge-warning">{pending.length} pending</span>
          <button className="btn btn-primary" onClick={openSubmitOnBehalf}>
            + Submit on Behalf
          </button>
        </div>
      </div>

      {message && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#16a34a' : '#dc2626'
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* ── Submit on Behalf Section ── */}
      {showSubmitOnBehalf && (
        <div className="card" style={{ marginBottom: 16, border: '2px solid #2563eb' }}>
          <div className="card-header" style={{ background: '#eff6ff' }}>
            <h3 style={{ color: '#2563eb' }}>📝 Submit Timesheet on Behalf of Employee</h3>
            <button className="btn btn-sm btn-outline" onClick={() => setShowSubmitOnBehalf(false)}>Cancel</button>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label>Team Member</label>
                <select className="form-control" value={selectedEmpId} onChange={e => setSelectedEmpId(e.target.value)}>
                  <option value="">— Select Employee —</option>
                  {teamMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.firstName} {m.lastName} ({m.employeeId})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Week Starting (Monday)</label>
                <input type="date" className="form-control" value={behalfWeek} onChange={e => setBehalfWeek(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                Time Entries — Total: <span style={{ color: '#16a34a' }}>{getBehalfGrandTotal().toFixed(1)}h</span>
              </span>
              <button className="btn btn-sm btn-outline" onClick={addBehalfRow}>+ Add Row</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '6px 4px', textAlign: 'left', minWidth: 130 }}>Project</th>
                    <th style={{ padding: '6px 4px', textAlign: 'left', minWidth: 120 }}>Activity</th>
                    <th style={{ padding: '6px 4px', textAlign: 'left', minWidth: 70 }}>TRC</th>
                    {dayNames.map(d => <th key={d} style={{ padding: '6px 2px', textAlign: 'center', width: 50 }}>{d}</th>)}
                    <th style={{ padding: '6px 4px', textAlign: 'center', width: 50 }}>Total</th>
                    <th style={{ width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {behalfEntries.map((row, rowIdx) => (
                    <tr key={rowIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '3px 4px' }}>
                        <select className="form-control" style={selectStyle} value={row.projectId}
                          onChange={e => updateBehalfField(rowIdx, 'projectId', e.target.value)}>
                          <option value="">—</option>
                          {projects.map(p => <option key={p.id} value={p.projectCode}>{p.projectCode}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '3px 4px' }}>
                        <select className="form-control" style={selectStyle} value={row.activityId}
                          onChange={e => updateBehalfField(rowIdx, 'activityId', e.target.value)}>
                          <option value="">—</option>
                          {activities.map(a => <option key={a.id} value={a.code}>{a.code}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '3px 4px' }}>
                        <select className="form-control" style={selectStyle} value={row.trcCode}
                          onChange={e => updateBehalfField(rowIdx, 'trcCode', e.target.value)}>
                          {trcCodes.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                        </select>
                      </td>
                      {row.hours.map((h, dayIdx) => (
                        <td key={dayIdx} style={{ padding: '3px 2px', textAlign: 'center' }}>
                          <input type="number" min="0" max="24" step="0.25"
                            value={h || ''} onChange={e => updateBehalfHour(rowIdx, dayIdx, e.target.value)}
                            placeholder="0"
                            style={{ width: 44, textAlign: 'center', padding: '3px 1px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 3 }}
                          />
                        </td>
                      ))}
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>
                        {getBehalfRowTotal(row).toFixed(1)}
                      </td>
                      <td>
                        <button className="btn btn-sm btn-danger" onClick={() => removeBehalfRow(rowIdx)}
                          disabled={behalfEntries.length <= 1} style={{ padding: '1px 6px', fontSize: 12 }}>−</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>Notes</label>
              <textarea className="form-control" rows={2} value={comments} onChange={e => setComments(e.target.value)}
                placeholder="Reason for submitting on behalf (optional)" />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-outline" onClick={() => setShowSubmitOnBehalf(false)}>Cancel</button>
              <button className="btn btn-info" onClick={() => submitOnBehalf('SAVE')} disabled={actionLoading === 'behalf'}>
                💾 Save as Draft
              </button>
              <button className="btn btn-primary" onClick={() => submitOnBehalf('SUBMIT')} disabled={actionLoading === 'behalf'}>
                ✅ Submit for Employee
              </button>
            </div>
          </div>
        </div>
      )}

      {pending.length === 0 ? (
        <div className="card">
          <div className="card-body empty-state">
            <p>No timesheets pending your approval</p>
          </div>
        </div>
      ) : (
        <div>
          {pending.map(ts => (
            <div className="card" key={ts.id} style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div>
                  <h3>{ts.employeeName}</h3>
                  <small style={{ color: '#6b7280' }}>
                    {ts.employeeEmpId} • {ts.employeeJobTitle} • {ts.teamName || 'No Team'}
                  </small>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span className={`badge ${getStatusBadge(ts.status)}`}>{ts.status?.replace(/_/g, ' ')}</span>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    {ts.weekStartDate} to {ts.weekEndDate}
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div>
                    <span style={{ fontSize: 28, fontWeight: 700 }}>
                      {editingId === ts.id ? getEditGrandTotal().toFixed(1) : ts.totalHours}h
                    </span>
                    <span style={{ color: '#6b7280', marginLeft: 8 }}>/ 40h scheduled</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    Submitted: {ts.submittedAt}
                  </div>
                </div>

                {/* ── VIEW MODE ── */}
                {editingId !== ts.id && ts.entries && ts.entries.length > 0 && (
                  <div className="table-wrapper" style={{ marginBottom: 16 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Project</th><th>Activity</th><th>TRC</th>
                          {dayNames.map(d => <th key={d}>{d}</th>)}
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ts.entries.map((e, i) => (
                          <tr key={i}>
                            <td style={{ fontSize: 12 }}>{e.projectId || '—'}</td>
                            <td style={{ fontSize: 12 }}>{e.activityId || '—'}</td>
                            <td>{e.trcCode}</td>
                            {(e.hours || []).map((h, d) => <td key={d}>{h || '—'}</td>)}
                            <td><strong>{e.totalHours}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── EDIT MODE ── */}
                {editingId === ts.id && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb' }}>✏️ Editing on behalf of {ts.employeeName}</span>
                      <button className="btn btn-sm btn-outline" onClick={addEditRow}>+ Add Row</button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ padding: '6px 4px', textAlign: 'left', minWidth: 130 }}>Project</th>
                            <th style={{ padding: '6px 4px', textAlign: 'left', minWidth: 120 }}>Activity</th>
                            <th style={{ padding: '6px 4px', textAlign: 'left', minWidth: 70 }}>TRC</th>
                            {dayNames.map(d => <th key={d} style={{ padding: '6px 2px', textAlign: 'center', width: 50 }}>{d}</th>)}
                            <th style={{ padding: '6px 4px', textAlign: 'center', width: 50 }}>Total</th>
                            <th style={{ width: 30 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {editEntries.map((row, rowIdx) => (
                            <tr key={rowIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '3px 4px' }}>
                                <select className="form-control" style={selectStyle} value={row.projectId}
                                  onChange={e => updateEditField(rowIdx, 'projectId', e.target.value)}>
                                  <option value="">—</option>
                                  {projects.map(p => <option key={p.id} value={p.projectCode}>{p.projectCode}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: '3px 4px' }}>
                                <select className="form-control" style={selectStyle} value={row.activityId}
                                  onChange={e => updateEditField(rowIdx, 'activityId', e.target.value)}>
                                  <option value="">—</option>
                                  {activities.map(a => <option key={a.id} value={a.code}>{a.code}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: '3px 4px' }}>
                                <select className="form-control" style={selectStyle} value={row.trcCode}
                                  onChange={e => updateEditField(rowIdx, 'trcCode', e.target.value)}>
                                  {trcCodes.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                                </select>
                              </td>
                              {row.hours.map((h, dayIdx) => (
                                <td key={dayIdx} style={{ padding: '3px 2px', textAlign: 'center' }}>
                                  <input type="number" min="0" max="24" step="0.25"
                                    value={h || ''} onChange={e => updateEditHour(rowIdx, dayIdx, e.target.value)}
                                    placeholder="0"
                                    style={{ width: 44, textAlign: 'center', padding: '3px 1px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 3 }}
                                  />
                                </td>
                              ))}
                              <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>
                                {getEditRowTotal(row).toFixed(1)}
                              </td>
                              <td>
                                <button className="btn btn-sm btn-danger" onClick={() => removeEditRow(rowIdx)}
                                  disabled={editEntries.length <= 1} style={{ padding: '1px 6px', fontSize: 12 }}>−</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── ACTION AREA ── */}
                {selectedId === ts.id ? (
                  <div>
                    <div className="form-group">
                      <label>Comments</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={comments}
                        onChange={e => setComments(e.target.value)}
                        placeholder="Add comments (required for rejection)"
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button className="btn btn-outline" onClick={() => { setSelectedId(null); setComments(''); cancelEdit(); }}>Cancel</button>
                      {editingId !== ts.id ? (
                        <>
                          <button className="btn btn-info" onClick={() => startEdit(ts)}>
                            ✏️ Edit on Behalf
                          </button>
                          <button className="btn btn-danger" onClick={() => handleAction(ts.id, 'reject')}
                            disabled={actionLoading === ts.id}>Reject</button>
                          <button className="btn btn-success" onClick={() => handleAction(ts.id, 'approve')}
                            disabled={actionLoading === ts.id}>Approve</button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-outline" onClick={cancelEdit}>Cancel Edit</button>
                          <button className="btn btn-info" onClick={() => saveEditAndKeep(ts)}
                            disabled={actionLoading === ts.id}>
                            💾 Save Edits
                          </button>
                          <button className="btn btn-success" onClick={() => saveEditAndApprove(ts)}
                            disabled={actionLoading === ts.id}>
                            ✅ Save & Approve
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={() => setSelectedId(ts.id)}>
                      Review & Action
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
